import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { FaSpinner } from "react-icons/fa";
import { BiSave, BiTransferAlt } from "react-icons/bi";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import styles from "../../SummaryInfoBlock.module.css";
import { MarkdownEditor } from "../MarkdownEditor/MarkdownEditor";
import { IDocDetails } from "../../../../interfaces/IDocDetails";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchTeamSummary } from "./services/fetchTeamSummary.service";
import { toast } from "react-toastify";
import { updateTeamSummary } from "./services/updateTeamSummary.service";
import { MdGTranslate } from "react-icons/md";
import { TranslateModal } from "../TranslateModal/TranslateModal";
import { IoCopyOutline } from "react-icons/io5";
import { AiOutlineEdit } from "react-icons/ai";
import { FiX } from "react-icons/fi";

interface ISummaryTabProps {
  editableData: any;
  editMode: any;
  setEditableData: (data: IDocDetails) => void;
  setEditMode: (mode: any) => void;
  updatedData: (data: Partial<IDocDetails>) => void;
  handleTranslate: (lang: string) => void;
  isTranslatedFields: boolean;
  loading: boolean;
  onShowOriginal: () => void;
  canUserEdit: boolean;
  discardSignal: number;
}

const SummaryTab: React.FC<ISummaryTabProps> = ({
  editableData,
  editMode,
  setEditableData,
  setEditMode,
  updatedData,
  handleTranslate,
  isTranslatedFields,
  loading,
  onShowOriginal,
  canUserEdit,
  discardSignal,
}) => {
  const { t } = useTranslation();
  const originalValuesRef = useRef<any>({});
  const [activeSummaryTab, setActiveSummaryTab] = useState("ai");
  const [copiedSummary, setCopiedSummary] = useState("");
  const [isEditTeamSummary, setIsEditTeamSummary] = useState(false);
  const [teamSummaryData, setTeamSummaryData] = useState<string | null>(null);
  const previousTeamSummaryRef = useRef<string | null>(null);
  const [showTranslate, setShowTranslate] = useState(false);
  const darkMode = localStorage.getItem("darkMode") === "true";
  const [currentLang, setCurrentLang] = useState<string | null>(null);
  const [copied, setCopied] = useState<Record<number, boolean>>({});
  const [copiedSummaryToBuffer, setCopiedSummaryToBuffer] = useState<
    Record<number, boolean>
  >({});
  const [disabledCancelTooltips, setDisabledCancelTooltips] = useState<
    Record<string, boolean>
  >({});
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false);
  const [pendingSummaryTab, setPendingSummaryTab] = useState<string | null>(
    null,
  );

  const { data: teamSummary, error } = useQuery({
    queryKey: ["data"],
    queryFn: () => fetchTeamSummary(editableData.code, editableData.id),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { mutate: updatedTeamSummary } = useMutation({
    mutationFn: (newSummary: string) =>
      updateTeamSummary(
        editableData.code,
        editableData.id,
        teamSummary.id,
        newSummary,
      ),
    onSuccess: (res: string) => {
      setTeamSummaryData(res);
      toast.success(t("docDetails.saveTeamSummary"));
    },
    onError: (error: any) => {
      setTeamSummaryData(previousTeamSummaryRef.current);
      toast.error(error?.message || "Update failed");
    },
  });

  const fields: Array<{ key: "short_summary" | "summary"; label: string }> = [
    { key: "short_summary", label: t("docDetails.shortSummary") },
    { key: "summary", label: t("docDetails.summary") },
  ];

  const summaryTabs =
    editableData.code === "lotus_notes"
      ? [{ label: t("docDetails.text"), key: "ai" }]
      : [
          { label: t("docDetails.aiSummary"), key: "ai" },
          ...(teamSummary && !error
            ? [{ label: t("docDetails.teamSummary"), key: "team" }]
            : []),
        ];

  useEffect(() => {
    if (activeSummaryTab === "team" && isEditTeamSummary) {
      setEditMode((prev: any) => ({ ...prev, summary: true }));
      setIsEditTeamSummary(false);
    }
  }, [activeSummaryTab, isEditTeamSummary]);

  useEffect(() => {
    if (activeSummaryTab === "team" && (!teamSummary || error)) {
      setActiveSummaryTab("ai");
    }
  }, [teamSummary, error]);

  useEffect(() => {
    if (teamSummary?.summary) {
      setTeamSummaryData(teamSummary.summary);
      setActiveSummaryTab("team");
    }
  }, [teamSummary]);

  const handleEditTeamSummary = (key: string) => {
    if (key === "short_summary") {
      originalValuesRef.current[key] = editableData[key] ?? "";
    } else {
      if (editableData.code === "lotus_notes") {
        originalValuesRef.current[key] = editableData.summary ?? "";
      } else {
        originalValuesRef.current[key] =
          teamSummaryData ?? teamSummary?.summary ?? "";
      }
    }
    if (document && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setEditMode((prev: any) => ({ ...prev, [key]: true }));

    setDisabledCancelTooltips((prev) => ({ ...prev, [key]: true }));
    setTimeout(() => {
      setDisabledCancelTooltips((prev) => ({ ...prev, [key]: false }));
    }, 300);
  };

  const revertTeamSummary = (key: string) => {
    if (originalValuesRef.current && key in originalValuesRef.current) {
      if (key === "short_summary") {
        setEditableData({
          ...editableData,
          [key]: originalValuesRef.current[key],
        });
      } else {
        if (editableData.code === "lotus_notes") {
          setEditableData({
            ...editableData,
            summary: originalValuesRef.current[key],
          });
        } else {
          setTeamSummaryData(originalValuesRef.current[key]);
        }
      }
    }
    setEditMode((prev: any) => ({ ...prev, [key]: false }));

    if (document && document.activeElement instanceof HTMLElement) {
      (document.activeElement as HTMLElement).blur();
    }
  };

  const handleSaveTeamSummary = (key: string) => {
    if (editableData.code === "lotus_notes" && key === "summary") {
      const prev = originalValuesRef.current[key] ?? "";
      const curr = editableData.summary ?? "";

      if (curr !== prev) {
        updatedData({ summary: curr });
      }

      setEditMode((prev: any) => ({ ...prev, summary: false }));
      return;
    }
    if (key === "short_summary") {
      const prev = originalValuesRef.current[key] ?? "";
      const curr = editableData[key] ?? "";

      if (curr !== prev) {
        updatedData({ [key]: curr });
      }
    } else {
      const prev = originalValuesRef.current[key] ?? "";
      const curr = teamSummaryData ?? "";

      if (curr !== prev) {
        previousTeamSummaryRef.current = prev;
        updatedTeamSummary(curr);
      }
    }

    setEditMode((prev: any) => ({ ...prev, [key]: false }));
  };

  const handleTranslateClick = () => {
    setShowTranslate(true);
  };

  const summaryEditKeys = ["summary", "short_summary"] as const;
  const hasUnsavedChanges = summaryEditKeys.some((key) => editMode[key]);

  const discardSummaryChanges = () => {
    if (editMode.short_summary) revertTeamSummary("short_summary");
    if (editMode.summary) revertTeamSummary("summary");
    setIsEditTeamSummary(false);
    setCopiedSummary("");
  };

  const requestSummaryTabChange = (nextTab: string) => {
    if (hasUnsavedChanges) {
      setPendingSummaryTab(nextTab);
      setIsUnsavedModalOpen(true);
      return;
    }
    setActiveSummaryTab(nextTab);
    setCopiedSummary("");
  };

  const confirmDiscardChanges = () => {
    discardSummaryChanges();
    setIsUnsavedModalOpen(false);
    if (pendingSummaryTab) {
      setActiveSummaryTab(pendingSummaryTab);
      setPendingSummaryTab(null);
    }
  };

  const keepEditing = () => {
    setIsUnsavedModalOpen(false);
    setPendingSummaryTab(null);
  };

  useEffect(() => {
    if (!discardSignal) return;
    discardSummaryChanges();
    setIsUnsavedModalOpen(false);
    setPendingSummaryTab(null);
  }, [discardSignal]);

  const onTranslate = (selectedLang: string) => {
    if (currentLang === selectedLang) {
      toast.info(t("translateModal.selectedLang"));
    } else {
      setCurrentLang(selectedLang);
      handleTranslate(selectedLang);
    }
  };

  const handleShowOriginal = () => {
    setCurrentLang(null);
    onShowOriginal();
  };

  return (
    <>
      {fields.map(
        ({ key, label }, index) =>
          Object.keys(editableData).length !== 0 && (
            <div
              key={key}
              className={`${
                index === fields.length - 1
                  ? ""
                  : "border-b-2 border-gray-500 dark:border-gray-300"
              } ${teamSummary ? "" : "mb-2"}  pb-2`}
            >
              {key === "summary" ? (
                <div className="flex title-base-color items-center justify-between">
                  <div className="flex items-center gap-2 w-4/5">
                    {editableData.code === "lotus_notes" ? (
                      <h4 className="text-base title-base-color">
                        {summaryTabs[0].label}
                      </h4>
                    ) : teamSummary && !error ? (
                      <div className="flex w-full border-b border-gray-300 dark:border-gray-600 justify-around">
                        {summaryTabs.map((tab, index) => (
                          <button
                            key={`summary-tab-${index}`}
                            onClick={() => requestSummaryTabChange(tab.key)}
                            className={`relative p-2 text-base title-base-color !font-normal w-1/2 transition-colors hover:bg-gray-300 dark:hover:bg-gray-700 border-b-2 ${
                              activeSummaryTab === tab.key &&
                              editableData.code !== "lotus_notes"
                                ? "border-del-green dark:border-white"
                                : "text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white"
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <h4 className="text-base title-base-color flex items-center justify-between">
                        {summaryTabs[0].label}
                      </h4>
                    )}
                  </div>
                  <div className="flex items-center">
                    {!editMode[key] && (
                      <>
                        {activeSummaryTab === "ai" && (
                          <>
                            <button
                              className={`tooltip-translate-${key} mr-1 ${isTranslatedFields ? "icon-green" : "icon-gray"}`}
                              onClick={() => handleTranslateClick()}
                            >
                              <MdGTranslate size={16} />
                            </button>
                            <Tooltip
                              anchorSelect={`.tooltip-translate-${key}`}
                              place="top"
                              content={t("translateModal.translate")}
                            />
                          </>
                        )}
                        <>
                          <IoCopyOutline
                            size={16}
                            onClick={async () => {
                              const text =
                                activeSummaryTab === "ai"
                                  ? String(editableData[key] ?? "")
                                  : String(teamSummaryData ?? "");
                              await navigator.clipboard.writeText(text);
                              setCopiedSummaryToBuffer((p) => ({
                                ...p,
                                [index]: true,
                              }));
                              setTimeout(
                                () =>
                                  setCopiedSummaryToBuffer((p) => ({
                                    ...p,
                                    [index]: false,
                                  })),
                                2000,
                              );
                            }}
                            className={`tooltip-summary-${index} icon-gray cursor-pointer focus:outline-none ml-0.5`}
                          />
                          <Tooltip
                            anchorSelect={`.tooltip-summary-${index}`}
                            clickable
                          >
                            {copiedSummaryToBuffer[index]
                              ? t("chatAI.copied")
                              : t("chatAI.copyText")}
                          </Tooltip>
                        </>

                        {canUserEdit &&
                          editableData.code !== "lotus_notes" &&
                          activeSummaryTab === "ai" &&
                          teamSummary && (
                            <button
                              className="tooltip-transfer ml-1 icon-gray"
                              onClick={() => {
                                const aiText = editableData.summary || "";
                                setCopiedSummary(aiText);
                                // save original so revert can restore it
                                originalValuesRef.current.summary =
                                  teamSummaryData ?? teamSummary?.summary ?? "";
                                // populate team summary data so repeated transfers replace it
                                setTeamSummaryData(aiText);
                                setIsEditTeamSummary(true);
                                setActiveSummaryTab("team");
                              }}
                            >
                              <BiTransferAlt size={16} />
                            </button>
                          )}

                        <Tooltip
                          anchorSelect=".tooltip-transfer"
                          place="top"
                          content={t("docDetails.copyToTeamSummary")}
                        />
                      </>
                    )}
                    {canUserEdit && editableData.code === "lotus_notes" && (
                      <div className="flex items-center">
                        {editMode[key] && (
                          <button
                            onClick={() => revertTeamSummary(key)}
                            className="text-gray-700 hover:text-gray-500 dark:text-white dark:hover:text-gray-300 icon-gray"
                            data-tooltip-id={`tooltip-cancel-${key}`}
                            data-tooltip-content={
                              disabledCancelTooltips[key]
                                ? ""
                                : t("feedback.cancel")
                            }
                          >
                            <FiX size={16} />
                          </button>
                        )}
                        <button
                          onClick={() =>
                            editMode[key]
                              ? handleSaveTeamSummary(key)
                              : handleEditTeamSummary(key)
                          }
                          className="ml-1 icon-gray"
                          data-tooltip-id={`tooltip-edit-save-${key}`}
                          data-tooltip-content={
                            editMode[key] ? t("saveTooltip") : t("editTooltip")
                          }
                        >
                          {editMode[key] ? (
                            <BiSave size={16} />
                          ) : (
                            <AiOutlineEdit size={16} />
                          )}
                        </button>
                        <Tooltip id={`tooltip-edit-save-${key}`} place="top" />
                        <Tooltip id={`tooltip-cancel-${key}`} place="top" />
                      </div>
                    )}
                    {canUserEdit &&
                      key === "summary" &&
                      activeSummaryTab === "team" && (
                        <div>
                          {editMode[key] ? (
                            <div className="flex items-center">
                              <button
                                onClick={() => revertTeamSummary(key)}
                                className="mr-1 text-gray-700 hover:text-gray-500 dark:text-white dark:hover:text-gray-300 icon-gray"
                                data-tooltip-id={`tooltip-cancel-${key}`}
                                data-tooltip-content={
                                  disabledCancelTooltips[key]
                                    ? ""
                                    : t("feedback.cancel")
                                }
                              >
                                <FiX size={16} />
                              </button>
                              <button
                                onClick={() => handleSaveTeamSummary(key)}
                                className="ml-0 icon-gray"
                                data-tooltip-id={`tooltip-save-${key}`}
                                data-tooltip-content={t("saveTooltip")}
                              >
                                <BiSave size={16} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditTeamSummary(key)}
                              className="tooltip-edit-save ml-1 pt-1 icon-gray"
                              data-tooltip-id={`tooltip-edit-${key}`}
                              data-tooltip-content={t("editTooltip")}
                            >
                              <AiOutlineEdit size={16} />
                            </button>
                          )}
                          <Tooltip id={`tooltip-edit-${key}`} place="top" />
                          <Tooltip id={`tooltip-save-${key}`} place="top" />
                          <Tooltip id={`tooltip-cancel-${key}`} place="top" />
                        </div>
                      )}
                  </div>
                </div>
              ) : (
                <h4 className="text-base title-base-color flex items-center justify-between">
                  {label}
                  <div className="flex items-center">
                    {!editMode[key] && (
                      <button
                        className={`tooltip-translate-${key} mr-1 ${isTranslatedFields ? "icon-green" : "icon-gray"}`}
                        onClick={() => handleTranslateClick()}
                      >
                        <MdGTranslate size={16} />
                      </button>
                    )}

                    <Tooltip
                      anchorSelect={`.tooltip-translate-${key}`}
                      place="top"
                      content={t("translateModal.translate")}
                    />
                    {!editMode[key] && (
                      <>
                        <IoCopyOutline
                          onClick={async () => {
                            await navigator.clipboard.writeText(
                              String(editableData[key] ?? ""),
                            );
                            setCopied((prev) => ({ ...prev, [index]: true }));
                            setTimeout(() => {
                              setCopied((prev) => ({
                                ...prev,
                                [index]: false,
                              }));
                            }, 2000);
                          }}
                          className={`tooltip-context-${index} icon-gray cursor-pointer focus:outline-none ml-0.5`}
                          size={16}
                        />
                        <Tooltip
                          anchorSelect={`.tooltip-context-${index}`}
                          clickable
                        >
                          {copied[index]
                            ? t("chatAI.copied")
                            : t("chatAI.copyText")}
                        </Tooltip>
                      </>
                    )}
                    {canUserEdit && (
                      <div>
                        {editMode[key] ? (
                          <div className="flex items-center">
                            <button
                              onClick={() => revertTeamSummary(key)}
                              className="mr-1 text-gray-700 hover:text-gray-500 dark:text-white dark:hover:text-gray-300 icon-gray"
                              data-tooltip-id={`tooltip-cancel-${key}`}
                              data-tooltip-content={
                                disabledCancelTooltips[key]
                                  ? ""
                                  : t("feedback.cancel")
                              }
                            >
                              <FiX size={16} />
                            </button>
                            <button
                              onClick={() => handleSaveTeamSummary(key)}
                              className="ml-0 icon-gray"
                              data-tooltip-id={`tooltip-save-${key}`}
                              data-tooltip-content={t("saveTooltip")}
                            >
                              <BiSave size={16} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEditTeamSummary(key)}
                            className="tooltip-edit-save ml-1 pt-1 icon-gray"
                            data-tooltip-id={`tooltip-edit-${key}`}
                            data-tooltip-content={t("editTooltip")}
                          >
                            <AiOutlineEdit size={16} />
                          </button>
                        )}
                        <Tooltip id={`tooltip-edit-${key}`} place="top" />
                        <Tooltip id={`tooltip-save-${key}`} place="top" />
                        <Tooltip id={`tooltip-cancel-${key}`} place="top" />
                      </div>
                    )}
                  </div>
                </h4>
              )}

              {editMode[key] ? (
                <div className="flex-grow w-full items-center bg-slate-100 dark:bg-[#2B3544] text-sm">
                  {editableData.code === "lotus_notes" && key === "summary" && (
                    <MarkdownEditor
                      data={editableData.summary ?? ""}
                      onChange={(data: string) =>
                        setEditableData({
                          ...editableData,
                          summary: data || "",
                        })
                      }
                      className="mdx-chat-editor"
                    />
                  )}
                  {key === "short_summary" && (
                    <MarkdownEditor
                      data={editableData[key] ?? ""}
                      onChange={(data: string) =>
                        setEditableData({
                          ...editableData,
                          [key]: data || "",
                        })
                      }
                      className="mdx-chat-editor"
                    />
                  )}
                  {key === "summary" && activeSummaryTab === "team" && (
                    <MarkdownEditor
                      data={teamSummaryData ?? copiedSummary ?? ""}
                      onChange={(data: string) => setTeamSummaryData(data)}
                      className="mdx-chat-editor"
                    />
                  )}
                </div>
              ) : loading ? (
                <div className="text-center p-4">
                  <FaSpinner
                    className="animate-spin inline-block mr-2"
                    size={24}
                    color={darkMode ? "#FFFFFF" : "#000000"}
                  />
                </div>
              ) : (
                <div className="mt-1 p-2 bg-slate-100 dark:bg-[#2B3544] rounded-lg">
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    className={
                      styles.markdownBlock + " text-secondary-color text-sm"
                    }
                  >
                    {key === "summary" && activeSummaryTab === "team"
                      ? (teamSummaryData ?? teamSummary?.summary ?? "")
                      : editableData[key]}
                  </Markdown>
                </div>
              )}
            </div>
          ),
      )}
      {isUnsavedModalOpen && (
        <div className="modal-overlay">
          <div className="modal-panel p-5 w-full max-w-md">
            <div className="flex items-start justify-between">
              <h4 className="text-base font-semibold">
                {t("docDetails.unsavedTitle", {
                  defaultValue: "Unsaved changes",
                })}
              </h4>
              <button
                className="modal-close-btn"
                onClick={keepEditing}
                aria-label={t("docDetails.unsavedKeep", {
                  defaultValue: "Keep editing",
                })}
              >
                ×
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              {t("docDetails.unsavedBody", {
                defaultValue:
                  "You have unsaved summary changes. Discard them to switch tabs.",
              })}
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={keepEditing}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50"
              >
                {t("docDetails.unsavedKeep", {
                  defaultValue: "Keep editing",
                })}
              </button>
              <button
                type="button"
                onClick={confirmDiscardChanges}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600"
              >
                {t("docDetails.unsavedDiscard", {
                  defaultValue: "Discard changes",
                })}
              </button>
            </div>
          </div>
        </div>
      )}
      <TranslateModal
        isOpen={showTranslate}
        onClose={() => setShowTranslate(false)}
        onShowOriginal={handleShowOriginal}
        onSelectTranslatedLang={onTranslate}
      />
    </>
  );
};

export { SummaryTab };
